import {
    useGetAssignmentFormsQuery,
    useGetFormsQuery,
    useLazyGetFormQuery
} from '../../../../api/services/formsService';
import React, {useEffect} from 'react';
import {Box, Button, Divider, MenuItem, TextField} from '@mui/material';
import {availableComponents} from '../FormBuilder/FormBuilder';
import {determinePossibleOperations} from '../FormBuilder/ShowFieldWhen';
import SettingsSectionBase, {SettingsSection} from './SettingsSection';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import {
    PARENT_AUDIENCE_TYPE,
    resolveBackendAudiencePayload
} from '../../../../constants/ParentAudienceType';
import EmailsInput from '../../../../shared-components/EmailsInput';
import {mergeArraysByKey} from '../../../../utility/mergeArraysByKey';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';

/* eslint-disable react/prop-types */

const triggerFormSettingsInitialState = {
    selectedForm: null,
    selectedFormSchema: null,
    relatedComponent: null,
    targetState: null,
    relatedFormEmailFieldId: null,
    relatedFormEmployeeFieldId: null,
};

/** Schema components that can hold an email (for "send trigger form to this field's value") */
const getEmailFieldsFromSchema = (schema) => {
    if (!Array.isArray(schema)) return [];
    return schema.filter(
        (c) =>
            c.type === 'previousSchoolEmail' ||
            (c.type === 'singleText' && c.inputType === 'email')
    );
};

const getEmployeeFieldsFromSchema = (schema) => {
    if (!Array.isArray(schema)) return [];
    return schema.filter((c) => c.type === 'employeeSelect');
};

export const sanitizeTriggerRecipientFieldIds = (audience, triggerSettings = {}) => ({
    ...triggerSettings,
    relatedFormEmailFieldId:
        audience === FORM_AUDIENCE_TYPE.externalUsers
            ? (triggerSettings.relatedFormEmailFieldId ?? null)
            : null,
    relatedFormEmployeeFieldId:
        audience === FORM_AUDIENCE_TYPE.employees
            ? (triggerSettings.relatedFormEmployeeFieldId ?? null)
            : null,
});

const updateTrigger = (wizardMode, prev, update) => {
    if (wizardMode) {
        return {
            ...prev,
            ...update,
        };
    }
    return {
        ...prev,
        triggerFormSettings: {
            ...prev.triggerFormSettings,
            ...update,
        }
    };
};

const isLinkedToStudentSourceForm = (form) =>
    form?.linkToStudents === true || form?.LinkToStudents === true;

const isLinkedToEmployeeSourceForm = (form) =>
    form?.linkToEmployees === true || form?.LinkToEmployees === true;

const isEnquiryForm = (form) =>
    Number(form?.parentAudienceType) === PARENT_AUDIENCE_TYPE.enquiry;

const filterFormsForAudience = (sourceForms = [], audience) => {
    const shouldShowEnquiryFormsOnly = audience === FORM_AUDIENCE_TYPE.enquiries;

    return sourceForms.filter((form) =>
        shouldShowEnquiryFormsOnly
            ? isEnquiryForm(form)
            : !isEnquiryForm(form)
    );
};

const sortFormsByName = (sourceForms = []) => [...sourceForms].sort((a, b) => {
    const nameA = String(a?.name ?? '').toLowerCase();
    const nameB = String(b?.name ?? '').toLowerCase();

    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;

    return Number(a?.id ?? 0) - Number(b?.id ?? 0);
});

const TriggerFormSettings = ({
                                  wizardMode = false,
                                  formId,
                                  audience,
                                  linkToStudents = false,
                                  linkToEmployees = false,
                                  settings,
                                  externalUsers,
                                  onSettingsChange
                              }) => {

    const triggerFormSettings = wizardMode
        ? settings
        : settings.triggerFormSettings || {};

    const externalEmails = wizardMode
        ? settings.externalEmails || []
        : settings.externalUserEmailAssignments || [];
    const originalExternalUsers = externalUsers || [];
    const isEnquiryTargetAudience = audience === FORM_AUDIENCE_TYPE.enquiries;
    const isEmployeeTargetAudience = audience === FORM_AUDIENCE_TYPE.employees;
    const shouldRequireLinkedStudentSourceForms =
        isEmployeeTargetAudience &&
        linkToStudents === true;
    const shouldRequireLinkedEmployeeSourceForms =
        isEmployeeTargetAudience &&
        linkToEmployees === true;
    const {
        data: forms = [],
        isLoading: isFormsLoading,
        isFetching: isFormsFetching
    } = useGetAssignmentFormsQuery({
        formId,
        queryParams: {
            rootAudienceType: resolveBackendAudiencePayload(audience, PARENT_AUDIENCE_TYPE.none).audience
        }
    }, {
        skip: isEmployeeTargetAudience || isEnquiryTargetAudience
    });
    const {
        data: enquiryFormsResponse,
        isLoading: isEnquiryFormsLoading,
        isFetching: isEnquiryFormsFetching
    } = useGetFormsQuery({
        pageSize: 1000,
        pageNumber: 0,
        filters: [{
            propertyName: 'parentAudienceType',
            value: PARENT_AUDIENCE_TYPE.enquiry,
            operation: 7
        }]
    }, {
        skip: !isEnquiryTargetAudience
    });
    const {
        data: employeeRootForms = [],
        isLoading: isEmployeeRootFormsLoading,
        isFetching: isEmployeeRootFormsFetching
    } = useGetAssignmentFormsQuery({
        formId,
        queryParams: {
            rootAudienceType: FORM_AUDIENCE_TYPE.employees
        }
    }, {
        skip: !isEmployeeTargetAudience
    });
    const {
        data: studentRootForms = [],
        isLoading: isStudentRootFormsLoading,
        isFetching: isStudentRootFormsFetching
    } = useGetAssignmentFormsQuery({
        formId,
        queryParams: {
            rootAudienceType: FORM_AUDIENCE_TYPE.students
        }
    }, {
        skip: !shouldRequireLinkedStudentSourceForms
    });
    const [getForm] = useLazyGetFormQuery();
    const mergeFormsById = React.useCallback(
        (sourceForms) => Array.from(
            new Map(sourceForms.map((form) => [form.id, form])).values()
        ),
        []
    );
    const availableForms = React.useMemo(
        () => {
            let sourceForms;

            if (isEnquiryTargetAudience) {
                sourceForms = enquiryFormsResponse?.items ?? [];
            } else if (!isEmployeeTargetAudience) {
                sourceForms = forms;
            } else if (shouldRequireLinkedStudentSourceForms) {
                sourceForms = mergeFormsById([
                    ...studentRootForms,
                    ...employeeRootForms.filter((form) => isLinkedToStudentSourceForm(form))
                ]);
            } else if (shouldRequireLinkedEmployeeSourceForms) {
                // Plain employee + employee-linked-to-employee sources (EmployeeId = linked subject).
                // Exclude employee-linked-to-student (subject is StudentId).
                sourceForms = employeeRootForms.filter((form) => !isLinkedToStudentSourceForm(form));
            } else {
                sourceForms = employeeRootForms.filter((form) =>
                    !isLinkedToStudentSourceForm(form) &&
                    !isLinkedToEmployeeSourceForm(form)
                );
            }

            return filterFormsForAudience(sourceForms, audience);
        },
        [
            employeeRootForms,
            audience,
            enquiryFormsResponse,
            forms,
            isEnquiryTargetAudience,
            isEmployeeTargetAudience,
            mergeFormsById,
            shouldRequireLinkedEmployeeSourceForms,
            shouldRequireLinkedStudentSourceForms,
            studentRootForms
        ]
    );
    const allSelectableForms = React.useMemo(
        () => {
            if (isEnquiryTargetAudience) {
                return enquiryFormsResponse?.items ?? [];
            }

            return isEmployeeTargetAudience
                ? mergeFormsById([...employeeRootForms, ...studentRootForms])
                : forms;
        },
        [
            employeeRootForms,
            enquiryFormsResponse,
            forms,
            isEnquiryTargetAudience,
            isEmployeeTargetAudience,
            mergeFormsById,
            studentRootForms
        ]
    );
    const selectedSourceForm = React.useMemo(() => {
        if (!triggerFormSettings.selectedForm) {
            return null;
        }

        return (
            allSelectableForms.find((form) => form.id === triggerFormSettings.selectedForm.id) ??
            triggerFormSettings.selectedForm
        );
    }, [allSelectableForms, triggerFormSettings.selectedForm]);
    const isAssignmentFormsLoading =
        (isEnquiryTargetAudience && (isEnquiryFormsLoading || isEnquiryFormsFetching)) ||
        (!isEnquiryTargetAudience && !isEmployeeTargetAudience && (isFormsLoading || isFormsFetching)) ||
        (isEmployeeTargetAudience && (isEmployeeRootFormsLoading || isEmployeeRootFormsFetching)) ||
        (shouldRequireLinkedStudentSourceForms && (isStudentRootFormsLoading || isStudentRootFormsFetching));
    const isSelectedSourceFormAllowed =
        !selectedSourceForm ||
        isAssignmentFormsLoading ||
        availableForms.some((form) => form.id === selectedSourceForm.id);

    useEffect(() => {
        if (
            !selectedSourceForm ||
            isSelectedSourceFormAllowed
        ) {
            return;
        }

        onSettingsChange(prev =>
            updateTrigger(wizardMode, prev, {
                ...triggerFormSettingsInitialState
            })
        );
    }, [
        isAssignmentFormsLoading,
        isSelectedSourceFormAllowed,
        onSettingsChange,
        selectedSourceForm,
        wizardMode
    ]);

    const formsWithSelected = React.useMemo(() => {
        if (!triggerFormSettings.selectedForm || !isSelectedSourceFormAllowed) {
            return sortFormsByName(availableForms);
        }

        const isSelectedInList = availableForms.some(f => f.id === triggerFormSettings.selectedForm.id);
        const selectedForms = isSelectedInList
            ? availableForms
            : [
            {
                id: triggerFormSettings.selectedForm.id,
                name: triggerFormSettings.selectedForm.name
            },
            ...availableForms
        ];

        return sortFormsByName(selectedForms);
    }, [
        availableForms,
        isSelectedSourceFormAllowed,
        triggerFormSettings.selectedForm
    ]);

    const sanitizedTriggerSettings = React.useMemo(
        () => sanitizeTriggerRecipientFieldIds(audience, triggerFormSettings),
        [audience, triggerFormSettings]
    );

    useEffect(() => {
        if (
            sanitizedTriggerSettings.relatedFormEmailFieldId ===
                (triggerFormSettings.relatedFormEmailFieldId ?? null) &&
            sanitizedTriggerSettings.relatedFormEmployeeFieldId ===
                (triggerFormSettings.relatedFormEmployeeFieldId ?? null)
        ) {
            return;
        }

        onSettingsChange((prev) =>
            updateTrigger(wizardMode, prev, {
                relatedFormEmailFieldId: sanitizedTriggerSettings.relatedFormEmailFieldId,
                relatedFormEmployeeFieldId: sanitizedTriggerSettings.relatedFormEmployeeFieldId,
            })
        );
    }, [
        audience,
        onSettingsChange,
        sanitizedTriggerSettings.relatedFormEmailFieldId,
        sanitizedTriggerSettings.relatedFormEmployeeFieldId,
        triggerFormSettings.relatedFormEmailFieldId,
        triggerFormSettings.relatedFormEmployeeFieldId,
        wizardMode,
    ]);

    const externalRecipientsReadOnly = !wizardMode &&
        audience === FORM_AUDIENCE_TYPE.externalUsers &&
        !!sanitizedTriggerSettings.relatedFormEmailFieldId;

    const handleExternalEmailsChange = (emails) => {
        const deduped = (emails || []).filter(
            (value, index, list) =>
                index === list.findIndex(
                    (item) => item.email?.toLowerCase() === value.email?.toLowerCase()
                )
        );

        if (!wizardMode) {
            onSettingsChange(prev => ({
                ...prev,
                externalUserEmailAssignments: mergeArraysByKey(
                    deduped,
                    originalExternalUsers,
                    'email'
                )
            }));
            return;
        }

        onSettingsChange(prev =>
            updateTrigger(wizardMode, prev, {
                externalEmails: deduped
            })
        );
    };

    const handleExternalEmailBlur = (email) => {
        if (!email) return;

        const nextEmails = [
            ...externalEmails,
            {email}
        ].filter(
            (value, index, list) =>
                index === list.findIndex(
                    (item) => item.email?.toLowerCase() === value.email?.toLowerCase()
                )
        );

        if (!wizardMode) {
            onSettingsChange(prev => ({
                ...prev,
                externalUserEmailAssignments: mergeArraysByKey(
                    nextEmails,
                    originalExternalUsers,
                    'email'
                )
            }));
            return;
        }

        onSettingsChange(prev =>
            updateTrigger(wizardMode, prev, {
                externalEmails: nextEmails
            })
        );
    };

    return (
        <>
            <SettingsSectionBase
                header={wizardMode ? undefined : 'Trigger'}
                headerIcon={wizardMode ? undefined : <BoltOutlinedIcon fontSize="small"/>}
                subHeader={
                    wizardMode ? undefined : 'Choose the form, question, and condition that will trigger this form'
                }
            >
                <SettingsSection>

                    <TextField
                        required
                        select
                        fullWidth
                        value={triggerFormSettings.selectedForm?.id || ''}
                        label="Form"
                        onChange={async e => {
                            const formId = e.target.value;
                            const response = await getForm(formId);
                            const form = response.data;

                            onSettingsChange(prev =>
                                updateTrigger(wizardMode, prev, {
                                    ...triggerFormSettingsInitialState,
                                    selectedForm: {
                                        id: form.id,
                                        name: form.name,
                                        schema: form.schema,
                                        audience: form.audience,
                                        assignmentType: form.assignmentType,
                                        accessLevel: form.accessLevel,
                                        linkToStudents: form.linkToStudents ?? form.LinkToStudents ?? false
                                    }
                                })
                            );
                        }}
                    >
                        {formsWithSelected?.map(({
                                                     id,
                                                     name
                                                 }) =>
                            <MenuItem key={id} value={id}>{name}</MenuItem>
                        )}
                    </TextField>

                    <TextField
                        select
                        value={triggerFormSettings.relatedComponent || ''}
                        disabled={!triggerFormSettings.selectedForm?.schema}
                        label="Question"
                        required
                        onChange={e =>
                            onSettingsChange(prev =>
                                updateTrigger(wizardMode, prev, {
                                    ...triggerFormSettings,
                                    relatedComponent: e.target.value
                                })
                            )
                        }
                    >
                        {triggerFormSettings.selectedForm?.schema
                            ?.filter(opt => availableComponents.includes(opt.type))
                            .map(opt => (
                                <MenuItem key={opt.id} value={opt}>
                                    {opt.label} - {opt.description}
                                </MenuItem>
                            ))}
                    </TextField>

                    <TextField
                        select
                        value={triggerFormSettings.targetState}
                        label="Equals"
                        required
                        disabled={!triggerFormSettings.relatedComponent}
                        onChange={e =>
                            onSettingsChange(prev =>
                                updateTrigger(wizardMode, prev, {
                                    targetState: e.target.value
                                })
                            )
                        }
                    >
                        {triggerFormSettings.relatedComponent &&
                            determinePossibleOperations(
                                triggerFormSettings.relatedComponent,
                                {checkboxOnlyChecked: true}
                            ).map(op => (
                                <MenuItem key={op.value} value={op.value}>
                                    {op.title}
                                </MenuItem>
                            ))}
                    </TextField>

                    {audience === FORM_AUDIENCE_TYPE.externalUsers &&
                        triggerFormSettings.selectedForm?.schema && (
                            <TextField
                                select
                                fullWidth
                                value={sanitizedTriggerSettings.relatedFormEmailFieldId || ''}
                                label="Email from field"
                                helperText="Optional. Pick a field from the trigger form that holds the recipient email. Leave empty to use the external user list."
                                onChange={e =>
                                    onSettingsChange(prev =>
                                        updateTrigger(wizardMode, prev, {
                                            relatedFormEmailFieldId:
                                                e.target.value || null
                                        })
                                    )
                                }
                            >
                                <MenuItem value="">None (use external user list)</MenuItem>
                                {getEmailFieldsFromSchema(
                                    triggerFormSettings.selectedForm.schema
                                ).map((c) => (
                                    <MenuItem key={c.id} value={c.id}>
                                        {c.description || c.label || c.id}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}

                    {audience === FORM_AUDIENCE_TYPE.employees &&
                        triggerFormSettings.selectedForm?.schema && (
                            <TextField
                                select
                                fullWidth
                                value={sanitizedTriggerSettings.relatedFormEmployeeFieldId || ''}
                                label="Employee from field"
                                helperText={
                                    linkToEmployees
                                        ? 'Optional. Pick who should submit. The linked employee (who the form is about) comes from the triggering form request. Required when the source form does not already identify a separate submitter.'
                                        : linkToStudents
                                            ? 'Optional. Pick a field from the trigger form that holds the recipient employee. The linked student comes from the triggering form request.'
                                            : 'Optional. Pick a field from the trigger form that holds the recipient employee. The triggered form will be sent to the selected employee when the trigger condition is met.'
                                }
                                onChange={e =>
                                    onSettingsChange(prev =>
                                        updateTrigger(wizardMode, prev, {
                                            relatedFormEmployeeFieldId:
                                                e.target.value || null
                                        })
                                    )
                                }
                            >
                                <MenuItem value="">None</MenuItem>
                                {getEmployeeFieldsFromSchema(
                                    triggerFormSettings.selectedForm.schema
                                ).map((c) => (
                                    <MenuItem key={c.id} value={c.id}>
                                        {c.description || c.label || c.id}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}

                    <Box textAlign="end">
                        <Button
                            variant="outlined"
                            onClick={() =>
                                onSettingsChange(prev =>
                                    updateTrigger(wizardMode, prev, {
                                        ...triggerFormSettingsInitialState
                                    })
                                )
                            }
                        >
                            Clear trigger settings
                        </Button>
                    </Box>

                </SettingsSection>
            </SettingsSectionBase>

            {!wizardMode && audience === FORM_AUDIENCE_TYPE.externalUsers && (
                <>
                    <Divider sx={{my: 5}}/>
                    <SettingsSectionBase
                        header="External User Recipients"
                        headerIcon={<PublicOutlinedIcon fontSize="small"/>}
                        subHeader={
                            externalRecipientsReadOnly
                                ? 'Recipients are taken from the selected email field above. Clear "Email from field" to enter addresses here.'
                                : 'Add email addresses for recipients without an account or linked profile.'
                        }
                    >
                        <SettingsSection>
                            <EmailsInput
                                values={externalEmails.filter(x => !x.isDeleted)}
                                onChange={handleExternalEmailsChange}
                                onBlur={handleExternalEmailBlur}
                                disabled={externalRecipientsReadOnly}
                            />
                        </SettingsSection>
                    </SettingsSectionBase>
                </>
            )}
        </>
    );
};

export default TriggerFormSettings;
